import {ButtonProps, closeDialog, openDialog, showNotification} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {equals, isNil, not} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {
  GetTransferOrderItemResponse,
  TransferOrderBasketItem,
  useCheckoutTransferOrderBasketMutation,
  useDeleteTransferOrderItemMutation,
  useDiscardTransferOrderBasketMutation,
  useGetTransferOrderBasketQuery,
  useLazyGetTransferOrderItemQuery,
  usePatchTransferOrderItemMutation,
  usePostTransferOrderItemMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  AfterSalesMaterialBasket,
  EitherQuantityOrError,
  FullScreenModal,
  handleApiError,
  logError,
  MaterialBasketItemEditingDetails,
  useBasketItemsQuantityState,
  usePermissions,
} from '@dms/shared';

import {TRANSFER_ORDER_ITEM_DETAIL_DIALOG} from '../constants/TransferOrderItemDetailDialog';
import {TransferOrderItemDetail} from './TransferOrderItemDetail';
import {TransferOrderMaterialList} from './TransferOrderMaterialList';

interface TransferOrderMaterialModalProps {
  transferOrderId: string;
  onActionClick: VoidFunction;
}

export function TransferOrderMaterialModal(props: TransferOrderMaterialModalProps) {
  const [canEditTransferOrder] = usePermissions({
    permissionKeys: ['transferOrderEdit'],
  });

  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: hasBasketError,
  } = useGetTransferOrderBasketQuery({
    transferOrderId: props.transferOrderId,
  });

  const [checkoutBasket, {isLoading: isCheckingOutBasket}] =
    useCheckoutTransferOrderBasketMutation();

  const [discardBasket, {isLoading: isDiscardingBasket}] = useDiscardTransferOrderBasketMutation();

  const [postTransferOrderItem, {isLoading: isAddingToBasket}] = usePostTransferOrderItemMutation();

  const [patchTransferOrderItem, {isLoading: isChangingQuantity}] =
    usePatchTransferOrderItemMutation();

  const [deleteTransferOrderItem, {isLoading: isDeletingItems}] =
    useDeleteTransferOrderItemMutation();

  const [getTransferOrderItem] = useLazyGetTransferOrderItemQuery();

  const basketItems = basket?.orderTransferBasketItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const basketTotalPrice = basket?.orderTransferBasketTotalPrice;

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingToBasket ||
    isCheckingOutBasket ||
    isDiscardingBasket ||
    isChangingQuantity ||
    isDeletingItems;

  const {setBasketItemQuantity, hasInvalidBasketItems, hasUnsettledQuantity} =
    useBasketItemsQuantityState(basketItems);

  const isCheckoutDisabled =
    not(canEditTransferOrder) || isBasketEmpty || hasPendingRequest || hasUnsettledQuantity;

  const isDiscardDisabled = not(canEditTransferOrder) || hasPendingRequest;

  const handleDiscard = () =>
    discardBasket({transferOrderId: props.transferOrderId})
      .unwrap()
      .then(props.onActionClick)
      .catch(handleApiError);

  const handleCheckout = () =>
    checkoutBasket({
      transferOrderId: props.transferOrderId,
    })
      .unwrap()
      .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.itemsAdded')))
      .then(props.onActionClick)
      .catch(handleApiError);

  const handleAddToBasket = async (articleId: string) => {
    await postTransferOrderItem({transferOrderId: props.transferOrderId, body: {articleId}})
      .unwrap()
      .catch(handleApiError);
  };

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setBasketItemQuantity(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await patchTransferOrderItem({
      transferOrderId: props.transferOrderId,
      transferOrderItemId: itemId,
      body: {quantity: quantity.newQuantity},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleEdit = async (editingDetails: MaterialBasketItemEditingDetails) => {
    if (isNil(editingDetails?.itemId)) {
      return logError("Missing itemId, can't open editing dialog");
    }

    const result = await getTransferOrderItem({
      transferOrderId: props.transferOrderId,
      transferOrderItemId: editingDetails.itemId,
    })
      .unwrap()
      .catch(handleApiError);

    const transferOrderItem =
      result?.transferOrderItem as GetTransferOrderItemResponse['transferOrderItem'];

    openDialog(
      <TransferOrderItemDetail
        transferOrderId={props.transferOrderId}
        transferOrderItemId={editingDetails.itemId}
        item={transferOrderItem}
        onClose={() => closeDialog(TRANSFER_ORDER_ITEM_DETAIL_DIALOG)}
      />,
      {
        id: TRANSFER_ORDER_ITEM_DETAIL_DIALOG,
        title: i18n.t('entity.warehouse.labels.editItem'),
        size: 'large',
        withAdditionalFooter: true,
      }
    );
  };

  const handleDelete = async (itemsIds: string | string[]) => {
    const ids = isArray(itemsIds) ? itemsIds : [itemsIds];

    const requestBody = {transferOrderItemIds: ids.map((id) => id)};

    await deleteTransferOrderItem({
      transferOrderId: props.transferOrderId,
      body: requestBody,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const actions: ButtonProps[] = [
    {
      isLoading: isDiscardingBasket,
      isDisabled: isDiscardDisabled,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleDiscard,
    },
    {
      isLoading: isCheckingOutBasket,
      isDisabled: isCheckoutDisabled,
      title: i18n.t('general.actions.add'),
      variant: 'primary',
      onClick: handleCheckout,
    },
  ];

  return (
    <FullScreenModal headline={i18n.t('general.actions.addMaterial')} actions={actions}>
      <Box padding={4} height="100%">
        <HStack spacing={4} height="100%">
          <Box flex={5}>
            <TransferOrderMaterialList
              transferOrderId={props.transferOrderId}
              onAddToBasket={handleAddToBasket}
              hasPendingRequest={hasPendingRequest}
            />
          </Box>
          <Box flex={1} minWidth={86}>
            <AfterSalesMaterialBasket<TransferOrderBasketItem>
              basket={{
                items: basketItems,
                totalPrice: basketTotalPrice,
                isLoading: isBasketLoading,
                hasError: hasBasketError,
                hasPendingRequest,
                hasInvalidItems: hasInvalidBasketItems,
              }}
              mechanic={{isMechanicHidden: true}}
              onQuantityChange={handleQuantityChange}
              onEdit={handleEdit}
              onDelete={handleDelete}
              data-testid="basket"
            />
          </Box>
        </HStack>
      </Box>
    </FullScreenModal>
  );
}
